import { ApiResponse } from '@/core/types';
import {
  SubscriptionPlansResponse,
  SubscribePlanRequest,
  SubscribePlanResponse,
  PlanConfigData,
  Promotion,
  CountryData,
  ChangePaymentMethodRequest,
  ChangePaymentMethodResponse,
  UserActiveStatsResponse,
  ValidateUpdateSubscriptionPlanRequest,
  ValidateUpdateSubscriptionPlanResponse,
  UpdateSubscriptionPlanResponse,
  UpdateSubscriptionPlanRequest,
  ValidatePromotionRequest,
  ValidatePromotionResponse,
  InvoiceHistoryListResponse,
  SubscriptionPlanPaymentRequest,
  SubscriptionPlanPaymentResponse, // Invoice History
} from '../types';
import {
  GET_PLAN_CONFIG,
  GET_SUBSCRIPTION_PLANS,
  SUBSCRIBE_PLAN,
  VALIDATE_PROMOTION,
  GET_CARD_CONNECT_HOST,
  GET_ALL_COUNTRIES,
  CHANGE_SUBSCRIPTION_PAYMENT_METHOD,
  VALIDATE_UPDATE_SUBSCRIPTION_PLAN,
  UPDATE_SUBSCRIPTION_PLAN,
  GET_USER_ACTIVE_STATS,
  GET_SUBSCRIPTION_INVOICE, // Invoice History
  PRINT_SUBSCRIPTION_INVOICE,
  VIEW_PDF_1500_FROM_ATP,
  SUBSCRIPTION_PLAN_PAYMENT,
} from '../constants';
import apiClient from '@/core/lib/api-client';

export const getPlanConfig = async (): Promise<ApiResponse<PlanConfigData>> => {
  const response =
    await apiClient.get<ApiResponse<PlanConfigData>>(GET_PLAN_CONFIG);
  return response;
};

/* #region Invoice History */
export async function getInvoiceHistory(
  page = 1,
  limit = 10
): Promise<ApiResponse<InvoiceHistoryListResponse>> {
  const requestData = {
    page,
    limit,
  };
  return await apiClient.post<ApiResponse<InvoiceHistoryListResponse>>(
    GET_SUBSCRIPTION_INVOICE,
    { requestData: requestData }
  );
}

export async function downloadInvoice(
  invoiceId: string
): Promise<ApiResponse<string>> {
  const requestData = { invoiceId: invoiceId };
  return await apiClient.post<ApiResponse<string>>(PRINT_SUBSCRIPTION_INVOICE, {
    requestData: requestData,
  });
}
/* #endregion Invoice History */

export const getSubscriptions = async (
  page: number = 1,
  limit: number = 1000,
  sortBy: string = 'LastUpdatedAt',
  sortOrder: 'asc' | 'desc' = 'desc'
): Promise<ApiResponse<SubscriptionPlansResponse>> => {
  const requestData = {
    page,
    limit,
    sortBy,
    sortOrder,
  };
  const response = await apiClient.post<ApiResponse<SubscriptionPlansResponse>>(
    GET_SUBSCRIPTION_PLANS,
    { requestData: requestData }
  );
  return response;
};

export const subscribePlan = async (
  requestData: SubscribePlanRequest
): Promise<ApiResponse<SubscribePlanResponse>> => {
  const response = await apiClient.post<ApiResponse<SubscribePlanResponse>>(
    SUBSCRIBE_PLAN,
    { requestData: requestData }
  );
  return response;
};

export const validatePromotion = async (
  requestData: ValidatePromotionRequest
): Promise<ApiResponse<ValidatePromotionResponse>> => {
  const response = await apiClient.post<ApiResponse<ValidatePromotionResponse>>(
    VALIDATE_PROMOTION,
    { requestData },
    { showGlobalLoading: false }
  );
  return response;
};

export const getCardConnectHost = async (): Promise<ApiResponse<string>> => {
  const response = await apiClient.get<ApiResponse<string>>(
    GET_CARD_CONNECT_HOST
  );
  return response;
};

export const getAllCountries = async (): Promise<
  ApiResponse<CountryData[]>
> => {
  const response =
    await apiClient.get<ApiResponse<CountryData[]>>(GET_ALL_COUNTRIES);
  return response;
};

export const changeSubscriptionPaymentMethod = async (
  requestData: ChangePaymentMethodRequest
): Promise<ApiResponse<ChangePaymentMethodResponse>> => {
  const response = await apiClient.post<
    ApiResponse<ChangePaymentMethodResponse>
  >(CHANGE_SUBSCRIPTION_PAYMENT_METHOD, { requestData });
  return response;
};

export const validateUpdateSubscriptionPlan = async (
  requestData: ValidateUpdateSubscriptionPlanRequest
): Promise<ApiResponse<ValidateUpdateSubscriptionPlanResponse>> => {
  const response = await apiClient.post<
    ApiResponse<ValidateUpdateSubscriptionPlanResponse>
  >(VALIDATE_UPDATE_SUBSCRIPTION_PLAN, { requestData });
  return response;
};

export const updateSubscriptionPlan = async (
  requestData: UpdateSubscriptionPlanRequest
): Promise<ApiResponse<UpdateSubscriptionPlanResponse>> => {
  const response = await apiClient.post<
    ApiResponse<UpdateSubscriptionPlanResponse>
  >(UPDATE_SUBSCRIPTION_PLAN, { requestData });
  return response;
};

export const getUserActiveStats = async (): Promise<
  ApiResponse<UserActiveStatsResponse>
> => {
  const response = await apiClient.post<ApiResponse<UserActiveStatsResponse>>(
    GET_USER_ACTIVE_STATS
  );
  return response;
};

export const subscriptionPlanPayment = async (
  requestData: SubscriptionPlanPaymentRequest
): Promise<ApiResponse<SubscriptionPlanPaymentResponse>> => {
  // // fake data response for testing
  // const fakeResponseSuccess: ApiResponse<SubscriptionPlanPaymentResponse> = {
  //   data: {
  //     success: true,
  //     message: 'Payment processed successfully',
  //   },
  //   errorCode: 0,
  //   serverInformation: {
  //     apiVersion: '',
  //     currentTime: 0,
  //     requestDuration: 0,
  //   },
  // };
  // const fakeResponseFailure: ApiResponse<SubscriptionPlanPaymentResponse> = {
  //   data: {
  //     success: false,
  //     message: 'Payment failed due to insufficient funds',
  //   },
  //   errorCode: 0,
  //   serverInformation: {
  //     apiVersion: '',
  //     currentTime: 0,
  //     requestDuration: 0,
  //   },
  // };

  // return fakeResponseSuccess;
  // return fakeResponseFailure;

  const response = await apiClient.post<
    ApiResponse<SubscriptionPlanPaymentResponse>
  >(SUBSCRIPTION_PLAN_PAYMENT, { requestData });
  return response;
};
